import React, { useState, useEffect, useCallback } from 'react';
import { supplierModel } from '../../../models/supplierModel';
import { useSupplierAuth } from '../../../context/SupplierAuthContext';
import { getSettings } from '../../../models/settingsModel';
import { toast } from 'react-toastify';
import '../../../styles/colors.css';

function SupplierInvitations() {
  const { supplier } = useSupplierAuth();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState(''); // For immediate UI updates
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [settings, setSettings] = useState(null);

  // Fetch invitations with pagination
  const fetchInvitations = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = {
        page: currentPage,
        pageSize: pageSize
      };
      
      if (searchTerm) params.search = searchTerm;
      
      const response = await supplierModel.getInvitations(params); 
      
      if (response.status === 1 || response.data) {
        // Handle the nested data structure from the API
        const responseData = response.data || response;
        const allInvitations = responseData.invitations || responseData.data || [];
        const meta = responseData.meta || {}; 
        
        // Client-side filtering for supplier if needed
        const supplierCode = supplier?.supplier_user_supplier_id || supplier?.BP_Code;
        let filteredInvitations = allInvitations;
        
        if (supplierCode) {
          filteredInvitations = allInvitations.filter(invitation => 
            isSupplierInvited(invitation, supplierCode)
          );     
         }
        
        // Client-side search filtering if search term exists
        if (searchTerm) {
          filteredInvitations = filteredInvitations.filter(invitation =>
            invitation.procurement_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            invitation.procurement_ref?.toLowerCase().includes(searchTerm.toLowerCase())
          ); 
           }
        
        // Client-side status filtering
        if (statusFilter !== 'all') {
          filteredInvitations = filteredInvitations.filter(invitation => {
            const status = getInvitationStatus(invitation);
            const isOpen = isRFQOpen(invitation.procurement_closing, invitation.procurement_closing_time);
            
            switch (statusFilter) {
              case 'open':
                return isOpen;
              case 'closed':
                return !isOpen;
              case 'submitted':
                return status.status === 'Submitted';
              case 'missed':
                return status.status === 'Missed';
              default:
                return true;
            }
          }); 
        }
        
        // Set total count from filtered data
        setTotalCount(filteredInvitations.length);
        
        // Client-side pagination - slice the filtered data
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedInvitations = filteredInvitations.slice(startIndex, endIndex);
       
        setInvitations(paginatedInvitations);
      } else {
        console.warn('API response does not indicate success:', response);
        setInvitations([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching invitations:', error);
      toast.error('Failed to load RFQ invitations');
      setInvitations([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, statusFilter, supplier]);

  useEffect(() => {
     fetchInvitations();
  }, [fetchInvitations]);

  // Reset to page 1 when search or status filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  // Debounce search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  // Load settings on component mount (now publicly accessible)
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Settings are now publicly accessible without authentication
        const settingsData = await getSettings();
        setSettings(settingsData); 
      } catch (error) {
        console.error('Error loading settings in invitations:', error);
        // Settings are optional - don't break the component if they fail
      }
    };
    loadSettings();
  }, []);

  const parseSuppliers = (supplierString) => {
    if (!supplierString) return [];
    
 
    
    // Add debugging
    if (typeof supplierString !== 'string') {
      console.warn('Supplier data is not a string:', typeof supplierString, supplierString);
      return [];
    }
    
    try {
      // Handle PHP serialized data
      if (supplierString.startsWith('a:')) { 
        // Basic PHP unserialize pattern matching for arrays
        const matches = supplierString.match(/s:\d+:"([^"]+)"/g);
        if (matches) {
          const parsed = matches.map(match => {
            const valueMatch = match.match(/s:\d+:"([^"]+)"/);
            return valueMatch ? valueMatch[1] : '';
          }); 
          return parsed;
        }
        return [];
      }
      
      // Handle JSON array
      if (supplierString.startsWith('[') || supplierString.startsWith('{')) { 
        const parsed = JSON.parse(supplierString); 
        return parsed;
      }
      
      // Handle comma-separated string
      if (supplierString.includes(',')) { 
        const parsed = supplierString.split(',').map(s => s.trim()).filter(s => s); 
        return parsed;
      }
       
      const parsed = [supplierString.trim()]; 
      return parsed;
      
    } catch (error) {
      console.warn('Could not parse suppliers:', error);
      console.warn('Original supplier string:', supplierString);
      return [];
    }
  };

  const isSupplierInvited = (invitation, supplierCode) => {
    if (!invitation || !supplierCode) { 
      return false;
    }
    
    try {
      const suppliers = parseSuppliers(invitation.specification_supplier);
      
      // Ensure both supplier code and suppliers in array are strings for comparison
      const supplierCodeStr = String(supplierCode).trim();
      const isInvited = Array.isArray(suppliers) && suppliers.some(s => String(s).trim() === supplierCodeStr);
      
     
      return isInvited;
    } catch (error) {
      console.warn('Error checking supplier invitation:', error);
      return false;
    }
  };

  const getInvitationStatus = (invitation) => {
    if (invitation.quote && Object.keys(invitation.quote).length > 0) {
      return { status: 'Submitted', className: 'bg-blue-100 text-blue-800' };
    } else {
      return { status: 'Missed', className: 'bg-red-100 text-red-800' };
    }
  };

  const getTimeRemaining = (closingDate, closingTime) => {
    if (!closingDate || !closingTime) return 'No closing date';
    
    try {
      // Parse date format (assuming DD/MM/YYYY)
      const dateParts = closingDate.split('/');
      if (dateParts.length === 3) {
        const formattedDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
        const closingDateTime = new Date(`${formattedDate} ${closingTime}`);
        const now = new Date();
        
        if (now > closingDateTime) {
          return `Closed ${closingDate} ${closingTime}`;
        } else {
          const diff = closingDateTime - now;
          const days = Math.floor(diff / (1000 * 60 * 60 * 24));
          const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          
          return `${days} days, ${hours} hours, ${minutes} minutes`;
        }
      }
    } catch (error) {
      console.error('Error parsing date:', error);
    }
    
    return `${closingDate} ${closingTime}`;
  };

  const handleSearch = (e) => {
    setSearchInput(e.target.value); // Update input immediately for UI responsiveness
  };

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const isRFQOpen = (closingDate, closingTime) => {
    if (!closingDate || !closingTime) return false;
    
    try {
      const dateParts = closingDate.split('/');
      if (dateParts.length === 3) {
        const formattedDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
        const closingDateTime = new Date(`${formattedDate} ${closingTime}`);
        return new Date() < closingDateTime;
      }
    } catch (error) {
      console.error('Error checking if RFQ is open:', error);
    }
    
    return false;
  };

  const handleViewRFQ = async (invitation) => {
    try { 
      
      // Validate required data
      if (!invitation || !invitation.procurement_ref) {
        console.error('Invitation or procurement_ref is missing');
        toast.error('RFQ data is not available');
        return;
      }
      
      const supplierCode = supplier?.supplier_user_supplier_id || supplier?.BP_Code;
      const supplierName = supplier?.supplier_user_name || supplier?.BP_Name;
      
      if (!supplierCode || !supplierName) {
        console.error('Supplier information is incomplete:', { supplierCode, supplierName });
        toast.error('Supplier information is incomplete');
        return;
      }
       
      
      // Import procurement service dynamically
      const procurementService = (await import('../../../services/procurementService')).default;
      
      // Use the procurement service to generate a secure supplier link
      const linkData = await procurementService.generateSupplierLink(
        invitation.procurement_ref,
        supplierCode,
        supplierName
      );
       
      
      // Handle the API response structure
      const responseData = linkData.data || linkData;
      
      // Use the current window location for the frontend URL
      const localBaseUrl = window.location.origin;
      const supplierUrl = `${localBaseUrl}/supplier/rfq/${responseData.encryptedRef}?SID=${responseData.encryptedSID}&S=${responseData.encryptedSupplierName}`;
       
      
      // Open in new tab
      window.open(supplierUrl, '_blank');
      
    } catch (error) {
      console.error('Error opening RFQ:', error);
      toast.error('Failed to open RFQ document');
    }
  };

  const formatPublishedDate = (timestamp) => {
    if (!timestamp) return '';
    
    // Check if it's a Unix timestamp (number)
    if (typeof timestamp === 'number' || !isNaN(timestamp)) {
      return new Date(parseInt(timestamp) * 1000).toLocaleDateString('en-GB');
    }
    
    // If it's already a date string, return as is
    return timestamp;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading RFQ invitations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-6">
          <div className="flex items-center justify-between">
            {/* Left side - Title and info */}
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">RFQ Invitations</h1>
                <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                  <span>Request for Quotation Invitations</span>
                  <span>•</span>
                  <span>{totalCount} items</span>
                  <span>•</span>
                  <span>Page {currentPage} of {totalPages}</span>
                  <span>•</span>
                  <span>Showing {Math.min(pageSize, totalCount)} of {totalCount}</span>
                </div>
              </div>
            </div>
            
            {/* Right side - Search */}
            <div className="flex items-center gap-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search requisitions..."
                  value={searchInput}
                  onChange={handleSearch}
                  className="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
              
              {/* Debug Button - Only show in development mode */}
              {settings?.debug_mode === '1' && (
                <button
                  onClick={() => setShowDebugModal(true)}
                  className="text-xs font-bold px-3 py-2 rounded-lg border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 transition"
                  title="Open Debug Information Modal"
                >
                  🐛 Debug
                </button>
              )}
            </div>
          </div>
          
          {/* Controls Row */}
          <div className="mt-6 pt-4 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Status:</label>
                <select 
                  value={statusFilter}
                  onChange={handleStatusFilter}
                  className="border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All</option>
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                  <option value="submitted">Submitted</option>
                  <option value="missed">Missed Submission</option>
                </select>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Show:</label>
              <select
                value={pageSize}
                onChange={handlePageSizeChange}
                className="border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="10">10 per page</option>
                <option value="20">20 per page</option>
                <option value="50">50 per page</option>
                <option value="100">100 per page</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Invitations Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Published
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Project Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Closing Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invitations.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-gray-500 text-lg">No RFQ invitations found.</p>
                      <p className="text-gray-400 text-sm mt-1">
                        {searchTerm ? `No results found for "${searchTerm}"` : "You'll see your invitations here when they're available."}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                invitations.map((invitation, index) => {
                  try { 

                    const invitationStatus = getInvitationStatus(invitation);
                    const timeRemaining = getTimeRemaining(invitation.procurement_closing, invitation.procurement_closing_time);
                    const isOpen = isRFQOpen(invitation.procurement_closing, invitation.procurement_closing_time);
                    const publishedDate = formatPublishedDate(invitation.procurement_time);

                    return (
                      <tr key={`${invitation.procurement_id || invitation.procurement_ref || index}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {publishedDate}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isOpen ? (
                            <button
                              className="text-blue-600 hover:text-blue-900 font-medium transition-colors duration-200"
                              onClick={() => handleViewRFQ(invitation)}
                            >
                              {invitation.procurement_title}
                            </button>
                          ) : (
                            <span className="text-gray-900 font-medium">
                              {invitation.procurement_title}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {timeRemaining}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${invitationStatus.className}`}>
                            {invitationStatus.status}
                          </span>
                        </td>
                      </tr>
                    );
                  } catch (error) {
                    console.warn('Error rendering invitation:', error);
                    return null;
                  }
                }).filter(Boolean) // Remove null entries
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {totalCount > 0 && (
          <div className="bg-gray-50 px-3 py-2 border-t border-gray-200 flex items-center justify-between text-xs">
            <div className="text-gray-500">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-2 py-1 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-2 py-1 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs"
              >
                Previous
              </button>
              <span className="px-2 py-1 border border-gray-300 bg-white text-gray-700 font-medium text-xs">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-2 py-1 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2 py-1 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Debug Modal */}
      {settings?.debug_mode === '1' && (
        <DebugModal 
          show={showDebugModal}
          onHide={() => setShowDebugModal(false)}
          invitations={invitations}
          supplier={supplier}
          allData={{
            invitations,
            supplier,
            settings,
            loading,
            token: localStorage.getItem('supplier_token')
          }}
        />
      )}
    </div>
  );
}

// Debug Modal Component for Invitations
const DebugModal = ({ show, onHide, invitations, supplier, allData }) => {
  const [selectedSection, setSelectedSection] = useState('overview');
  
  if (!show) return null;

  const dataStructure = {
    overview: {
      title: 'Overview',
      icon: '📋',
      data: {
        supplier_name: supplier?.supplier_user_name || supplier?.Contact_Person || supplier?.BP_Name,
        supplier_email: supplier?.supplier_user_email || supplier?.E_Mail,
        supplier_id: supplier?.supplier_user_supplier_id || supplier?.VendorID || supplier?.BP_Code,
        invitations_count: invitations?.length || 0,
        token_exists: !!localStorage.getItem('supplier_token'),
        loading_state: allData?.loading
      }
    },
    invitations: {
      title: 'Invitations Data',
      icon: '📧',
      data: invitations || []
    },
    supplier_context: {
      title: 'Supplier Context',
      icon: '👤',
      data: supplier || {}
    },
    settings: {
      title: 'Settings',
      icon: '⚙️',
      data: {
        settings_loaded: !!allData?.settings,
        site_logo_preview: allData?.settings?.data?.site_logo_preview || 'Not available',
        site_name: allData?.settings?.data?.site_name || 'Not available',
        raw_settings: allData?.settings || {}
      }
    },
    authentication: {
      title: 'Authentication',
      icon: '🔐',
      data: {
        token: allData?.token ? 'Present' : 'Missing',
        token_length: allData?.token?.length || 0,
        localStorage_keys: Object.keys(localStorage),
        supplier_login_status: supplier ? 'Logged in' : 'Not logged in'
      }
    },
    raw: {
      title: 'Raw Data',
      icon: '🔧',
      data: allData
    }
  };

  return (
    <div className={`fixed inset-0 z-50 ${show ? 'block' : 'hidden'}`}>
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onHide}></div>
      <div className="relative bg-white rounded-lg shadow-xl max-w-7xl mx-auto mt-4 mb-4 h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-red-600 text-white p-4 flex justify-between items-center flex-shrink-0">
          <h3 className="text-lg font-semibold flex items-center">
            <svg className="inline-block mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Debug Information - Supplier Invitations
          </h3>
          <button 
            onClick={onHide}
            className="text-white hover:text-gray-200 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-1 overflow-hidden">
          {/* Navigation Sidebar */}
          <div className="w-1/4 border-r border-gray-300 bg-gray-50 overflow-y-auto flex-shrink-0">
            <div className="p-4">
              <h6 className="text-sm font-bold mb-4 uppercase tracking-wider text-gray-700">
                Data Sections
              </h6>
              <div className="space-y-1">
                {Object.entries(dataStructure).map(([key, section]) => (
                  <button
                    key={key}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 text-sm font-medium ${
                      selectedSection === key
                        ? 'bg-red-500 text-white shadow-sm'
                        : 'text-gray-700 hover:bg-white hover:text-gray-900 hover:shadow-sm'
                    }`}
                    onClick={() => setSelectedSection(key)}
                  >
                    <span className="mr-2">{section.icon}</span>
                    {section.title}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-hidden flex flex-col bg-white">
            <div className="p-6 flex-shrink-0 border-b border-gray-200">
              <div className="flex items-center mb-4">
                <span className="text-2xl mr-3">{dataStructure[selectedSection]?.icon}</span>
                <h4 className="text-xl font-semibold text-gray-900">
                  {dataStructure[selectedSection]?.title}
                </h4>
              </div>
            </div>
            
            <div className="flex-1 overflow-hidden px-6 pb-6">
              <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm h-full overflow-auto border border-gray-300">
                <pre className="whitespace-pre-wrap text-gray-100">
                  {JSON.stringify(dataStructure[selectedSection]?.data, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierInvitations;
